import Table, { TableBody } from '@ingka/table';
import Text from '@ingka/text';
import { useFormatWithLocale, useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { OccurrenceType, RetailUnit } from '@lotta/shared/types-lottery-schemas';
import { getTotalDrawsForLottery } from '@lotta/shared/util-draws';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import { useContext } from 'react';
import { ConfigContext } from '@lotta/self-service/lottery-fe/util-config';
import { isMaxWinnersPerStoreConstraint } from '@lotta/util-lottery-type-guards';
import { Description } from '../Description';
import styles from '../LotteryConfigOverview.module.scss';
import type { LotteryConfigData } from '../types';

/**
 * Get long name of day of week from week day number (1-7)
 *
 * @param locale Eg 'en-US' or 'se-SV'
 * @param day 1-7 (MON-SUN)
 * @returns Long name of the weekday, eg 'Monday' or 'Sunday'
 */
const getDayOfWeek = (locale: string, day: number): string => {
  const formatWeekDay = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format;
  // 1st May 2023 is a Monday (May is monthIndex 4)
  return formatWeekDay(new Date(2023, 4, day));
};

const DrawDay: React.FC<{ occurrenceType: OccurrenceType; drawDay: number }> = ({
  occurrenceType,
  drawDay,
}) => {
  const { locale } = useLocale();

  switch (occurrenceType) {
    case 'WEEKLY':
      return <Text>{getDayOfWeek(locale, drawDay)}</Text>;
    case 'MONTHLY':
    // fallthrough
    default:
      return <Text>{drawDay}</Text>;
  }
};

const DrawDayLabel: React.FC<{ occurrenceType: OccurrenceType }> = ({ occurrenceType }) => {
  const { t } = useTranslation();
  switch (occurrenceType) {
    case 'MONTHLY':
      return <Text>{t('lottery-form-draw-day-stepper-label')}</Text>;
    case 'WEEKLY':
      return <Text>{t('lottery-form-draw-day-of-week')}</Text>;
    case 'SINGLE':
      return <Text>{t('lottery-form-draw-day-stepper-label')}</Text>;
    default:
      return <Text />;
  }
};

export const InformationTable: React.FC<{
  values: LotteryConfigData;
  currency: string;
  storesDictionary: Record<string, string>;
  timezone: string;
}> = ({ values: { lottery }, currency, storesDictionary, timezone }) => {
  const {
    lotteryTemplateId,
    name,
    description,
    drawDay,
    drawTime,
    occurrence,
    nbrWinners,
    drawConstraints,
    dataSource,
    cmp,
    swipe,
    simpleSignup,
  } = lottery;
  const { t } = useTranslation();
  const { lotteryId } = useParams();
  const { retailUnit } = useLocale();
  const { data: retailUnitData } = useGetRetailUnitQuery({ retailUnit });
  const { ikeaDomain } = useContext(ConfigContext);
  const { dateWithLocale } = useFormatWithLocale();

  let storeIds: string[] | undefined;
  let maxNbrWinnersPerStore: number | undefined;

  const maxNbrWinnersPerStoreConstraint = drawConstraints?.find(isMaxWinnersPerStoreConstraint);
  if (maxNbrWinnersPerStoreConstraint) {
    storeIds = maxNbrWinnersPerStoreConstraint.storeIds;
    maxNbrWinnersPerStore = maxNbrWinnersPerStoreConstraint.maxNbrWinnersPerStore;
  }

  const retailChannels =
    dataSource?.dataSourceType === 'PURCHASE'
      ? dataSource.retailChannels.includes('ALL')
        ? ['ALL']
        : dataSource.retailChannels
      : undefined;

  const minimumPurchaseAmount =
    dataSource?.dataSourceType === 'PURCHASE' ? dataSource.minimumPurchaseAmount : undefined;

  const generatePurchaseTypesInfo = (retailChannels: string[]) => {
    const retailChannelTranslationKeys = {
      ALL: 'general-purchase-types-all',
      STORE: 'general-purchase-types-store',
      ONLINE: 'general-purchase-types-online',
      REMOTE: 'general-purchase-types-remote',
    };

    const foundChannels = retailChannels
      .filter((channel) => channel in retailChannelTranslationKeys)
      .map((channel) =>
        t(retailChannelTranslationKeys[channel as keyof typeof retailChannelTranslationKeys])
      );

    return foundChannels.join('\n ');
  };

  const generateTemplateTypeInfo = (lotteryTemplateId: string) => {
    if (lotteryTemplateId === 'monthly-purchase') {
      return t('template-purchase-contest-title');
    } else {
      return t(`template-${lotteryTemplateId}-contest-title`);
    }
  };

  const generateSwipeGameUrls = (id: string, retailUnit: RetailUnit) => {
    // eslint-disable-next-line i18next/no-literal-string
    const languages = retailUnit.languages;
    const urls = languages?.map((language) => (
      <tr key={'SwipeUrl' + language}>
        <td className={styles['heading']}>
          <Text>{t('lottery-details-swipe-url', { language: language.toUpperCase() })}</Text>
        </td>
        <td>
          <Text>
            {`https://${ikeaDomain}/${retailUnit.id.toLowerCase()}/${language}/ikea-family/contest/sign-up/swipe?lotteryId=${encodeURIComponent(
              id
            )}`}
          </Text>
        </td>
      </tr>
    ));
    return urls;
  };

  const generateSimpleSignupUrls = (id: string, retailUnit: RetailUnit) => {
    // eslint-disable-next-line i18next/no-literal-string
    const languages = retailUnit.languages;
    const urls = languages?.map((language) => (
      <tr key={'SignupUrl' + language}>
        <td className={styles['heading']}>
          <Text>
            {t('lottery-details-simple-signup-url', { language: language.toUpperCase() })}
          </Text>
        </td>
        <td>
          <Text>
            {`https://${ikeaDomain}/${retailUnit.id.toLowerCase()}/${language}/ikea-family/contest/sign-up/simple?lotteryId=${encodeURIComponent(
              id
            )}`}
          </Text>
        </td>
      </tr>
    ));
    return urls;
  };

  const storeNames = storeIds?.map((id: string) => storesDictionary[id] ?? id).join(', ');
  return (
    <Table>
      <TableBody className={styles['tbody']}>
        <tr key="templateName">
          <td className={styles['heading']}>
            <Text>{t('general-template')}</Text>
          </td>
          <td>
            <Text>{generateTemplateTypeInfo(lotteryTemplateId)}</Text>
          </td>
        </tr>
        <tr key="lotteryName">
          <td className={styles['heading']}>
            <Text>{t('general-lottery-name')}</Text>
          </td>
          <td>
            <Text>{name}</Text>
          </td>
        </tr>
        <Description text={description} />
        <tr key="startDate">
          <td className={styles['heading']}>
            <Text>{t('lottery-details-start-date')}</Text>
          </td>
          <td>
            <Text>{dateWithLocale(occurrence.startDate)}</Text>
          </td>
        </tr>
        <tr key="endDate">
          <td className={styles['heading']}>
            <Text>{t('lottery-details-end-date')}</Text>
          </td>
          <td>
            <Text>{dateWithLocale(occurrence.endDate)}</Text>
          </td>
        </tr>
        <tr key="drawDay">
          <td className={styles['heading']}>
            <DrawDayLabel occurrenceType={occurrence.occurrenceType} />
          </td>
          <td>
            <DrawDay occurrenceType={occurrence.occurrenceType} drawDay={drawDay} />
          </td>
        </tr>
        <tr key="drawTime">
          <td className={styles['heading']}>
            <Text>{t('lottery-form-draw-time-dropdown')}</Text>
          </td>
          <td>
            <Text>{`${drawTime} (${timezone})`}</Text>
          </td>
        </tr>
        <tr key="nbrWinnersDraw">
          <td className={styles['heading']}>
            <Text>{t('general-number-winners-draw')}</Text>
          </td>
          <td>
            <Text>{nbrWinners}</Text>
          </td>
        </tr>
        <tr key="nbrDraws">
          <td className={styles['heading']}>
            <Text>{t('general-number-of-draws')}</Text>
          </td>
          <td>
            <Text>{getTotalDrawsForLottery(occurrence)}</Text>
          </td>
        </tr>
        {retailChannels && (
          <tr key="purchaseTypes">
            <td className={styles['heading']}>
              <Text>{t('general-purchase-types')}</Text>
            </td>
            <td className={styles['body']}>
              <Text>{generatePurchaseTypesInfo(retailChannels)}</Text>
            </td>
          </tr>
        )}
        {minimumPurchaseAmount && (
          <tr key="minimumPurchaseAmount">
            <td className={styles['heading']}>
              <Text>{t('general-minimum-purchase-amount')}</Text>
            </td>
            <td>
              <Text>{`${minimumPurchaseAmount} ${currency}`}</Text>
            </td>
          </tr>
        )}
        {maxNbrWinnersPerStore !== undefined && (
          <tr key="nbrWinnersStore">
            <td className={styles['heading']}>
              <Text>{t('general-max-number-of-winners-per-store')}</Text>
            </td>
            <td>
              <Text>{maxNbrWinnersPerStore}</Text>
            </td>
          </tr>
        )}
        {storeNames && (
          <tr key="stores">
            <td className={styles['heading']}>
              <Text>{t('general-store-names')}</Text>
            </td>
            <td>
              <Text>{storeNames}</Text>
            </td>
          </tr>
        )}
        {cmp?.days && (
          <tr key="cmpDays">
            <td className={styles['heading']}>
              <Text>{t('general-days-to-claim-prize')}</Text>
            </td>
            <td>
              <Text>{cmp.days}</Text>
            </td>
          </tr>
        )}

        {swipe &&
          lotteryId &&
          retailUnitData &&
          generateSwipeGameUrls(lotteryId, retailUnitData.retailUnit)}
        {simpleSignup &&
          lotteryId &&
          retailUnitData &&
          generateSimpleSignupUrls(lotteryId, retailUnitData.retailUnit)}
      </TableBody>
    </Table>
  );
};
